import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@/lib/logger';
import { AlertCircle } from 'lucide-react';

interface BlockErrorBoundaryProps {
  children: ReactNode;
  /** User-friendly message shown when the block fails to render */
  fallbackMessage?: string;
}

interface BlockErrorBoundaryState {
  hasError: boolean;
}

/**
 * Lightweight error boundary for individual chat blocks and messages.
 *
 * When a block renderer crashes (e.g. malformed LLM JSON), this boundary
 * catches the error and renders a small, user-friendly fallback card
 * instead of crashing the entire chat UI.
 */
class BlockErrorBoundary extends Component<BlockErrorBoundaryProps, BlockErrorBoundaryState> {
  constructor(props: BlockErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): BlockErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger(error, {
      context: 'BlockErrorBoundary',
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2.5">
          <AlertCircle className="size-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {this.props.fallbackMessage ?? 'Something went wrong while displaying this content.'}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default BlockErrorBoundary;
